import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { Layers, Plus, Pencil, Trash2, Check, X } from "lucide-react";

export const ThreadSwitcher = ({ companionId, activeThreadId, onPick, onThreadsChanged }) => {
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const load = async () => {
    const { data } = await api.get(`/companions/${companionId}/threads`);
    setThreads(data);
    return data;
  };

  useEffect(() => { if (open) load().catch(() => {}); /* eslint-disable-next-line */ }, [open, companionId]);

  const create = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      const { data } = await api.post(`/companions/${companionId}/threads`, { title: newTitle.trim() });
      setNewTitle("");
      const list = await load();
      onThreadsChanged?.(list);
      onPick?.(data.thread_id);
      setOpen(false);
      toast.success(`Arc created: ${data.title}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const rename = async (tid) => {
    if (!editTitle.trim()) { setEditing(null); return; }
    try {
      await api.patch(`/companions/${companionId}/threads/${tid}`, { title: editTitle.trim() });
      setEditing(null);
      const list = await load();
      onThreadsChanged?.(list);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (tid) => {
    if (threads.length === 1) { toast.error("You need at least one arc"); return; }
    if (!window.confirm("Delete this arc and all its messages? This can't be undone.")) return;
    try {
      await api.delete(`/companions/${companionId}/threads/${tid}`);
      const list = await load();
      onThreadsChanged?.(list);
      if (tid === activeThreadId && list.length) onPick?.(list[0].thread_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const active = threads.find((t) => t.thread_id === activeThreadId);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="threads-trigger"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 h-8 px-3 text-[11px] font-mono uppercase tracking-widest text-slate-200"
        >
          <Layers className="h-3 w-3" />
          {active ? active.title : "Arcs"}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-sm bg-[#0F1117] border-white/10">
        <SheetHeader>
          <SheetTitle className="font-serif text-xl">Story arcs</SheetTitle>
          <SheetDescription className="text-xs text-slate-500">Switch between ongoing storylines with this companion. Each arc has its own memory.</SheetDescription>
        </SheetHeader>
        <form onSubmit={create} className="mt-5 flex items-center gap-2">
          <Input
            data-testid="thread-new-title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="e.g. Mafia wedding arc"
            maxLength={80}
            className="bg-[#07080A] border-white/10"
          />
          <Button data-testid="thread-new-submit" type="submit" size="icon" className="rounded-full bg-amber-500 hover:bg-amber-400 text-black h-9 w-9">
            <Plus className="h-4 w-4" />
          </Button>
        </form>
        <div data-testid="threads-list" className="mt-5 space-y-2">
          {threads.map((t) => (
            <div
              key={t.thread_id}
              data-testid={`thread-item-${t.thread_id}`}
              className={`rounded-xl border p-3 flex items-center gap-2 transition ${t.thread_id === activeThreadId ? "border-amber-500/60 bg-amber-500/10" : "border-white/10 bg-[#07080A] hover:border-white/20"}`}
            >
              {editing === t.thread_id ? (
                <>
                  <Input
                    autoFocus
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="bg-[#07080A] border-white/10 h-8"
                    maxLength={80}
                  />
                  <button onClick={() => rename(t.thread_id)} className="rounded-full p-1 text-emerald-400 hover:bg-white/5"><Check className="h-4 w-4" /></button>
                  <button onClick={() => setEditing(null)} className="rounded-full p-1 text-slate-500 hover:bg-white/5"><X className="h-4 w-4" /></button>
                </>
              ) : (
                <>
                  <button
                    data-testid={`thread-pick-${t.thread_id}`}
                    onClick={() => { onPick?.(t.thread_id); setOpen(false); }}
                    className="flex-1 text-left min-w-0"
                  >
                    <div className="font-serif text-sm font-semibold text-slate-100 truncate">{t.title}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">{t.message_count} msgs · {new Date(t.updated_at).toLocaleDateString()}</div>
                  </button>
                  <button
                    data-testid={`thread-rename-${t.thread_id}`}
                    onClick={() => { setEditing(t.thread_id); setEditTitle(t.title); }}
                    className="rounded-full p-1.5 text-slate-500 hover:text-amber-300 hover:bg-white/5"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`thread-delete-${t.thread_id}`}
                    onClick={() => remove(t.thread_id)}
                    className="rounded-full p-1.5 text-slate-500 hover:text-rose-400 hover:bg-white/5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}
          {threads.length === 0 && (
            <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-xs text-slate-500">
              No arcs yet — create your first one above.
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
